import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useSmartScroll } from "@/lib/useSmartScroll";

const TIERS = [
  {
    id: "tier-1",
    kicker: "Tier.01",
    name: "Single-Problem Fix",
    price: "$3,500",
    priceHigh: "$5,000",
    unit: "one-time",
    delivery: "5 days",
    tagline: "Kill one bottleneck. Fast.",
    includes: [
      "One focused engine module (e.g. inventory only)",
      "Your brand · your workflow · your data",
      "Trained team on day one",
      "30-day post-launch monitoring",
    ],
    cta: "Request Fix",
    feature: false,
  },
  {
    id: "tier-2",
    kicker: "Tier.02",
    name: "The Core Ecosystem",
    price: "$8,500",
    priceHigh: "$12,000",
    unit: "one-time",
    delivery: "14 days",
    tagline: "Full engine. Field, shop, owner — one surface.",
    includes: [
      "Complete engine of your industry template",
      "Field ⇄ shop ⇄ owner communication ledger",
      "KPI dashboard + audit-ready reporting",
      "60-day post-launch monitoring + tuning",
      "Owner training session · 2hrs",
    ],
    cta: "Request Ecosystem",
    feature: true,
  },
  {
    id: "retainer",
    kicker: "Fuel.Line",
    name: "Engine Care - Monthly",
    price: "$149.00",
    unit: "starting at · monthly",
    delivery: "ongoing",
    tagline: "Hosting, monitoring, minor logic updates.",
    includes: [
      "Managed cloud hosting + 24/7 uptime",
      "Continuous monitoring & security patches",
      "Minor logic updates + report changes",
      "Priority response · owner Slack channel",
      "Quarterly business review with data",
    ],
    cta: "Add Engine Care",
    feature: false,
  },
];

const Pricing = () => {
  const smart = useSmartScroll();

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 lg:py-36">
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-8 bg-nex-gold" />
              <span className="mono-label">// Pricing · Value-Based, Not Hour-Based</span>
            </div>
            <h2 className="serif-head text-4xl md:text-6xl mb-6">
              We price the <span className="italic text-nex-gold">time we save you.</span>
              <br /> Not the hours we spend.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-nex-chrome text-lg leading-relaxed">
              Because we deploy in weeks — not quarters — every day you don't have your engine is a day of leaked margin. Our pricing reflects the value of that saved time, not the cost of ours.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`pricing-${t.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.19, 1, 0.22, 1] }}
              className={`card-industrial corner-tick p-8 flex flex-col relative ${
                t.feature ? "border-nex-gold/60 md:-translate-y-3" : ""
              }`}
            >
              {t.feature && (
                <div className="absolute -top-3 left-8 bg-nex-gold text-nex-navy px-3 py-1 mono-body text-[10px] uppercase tracking-[0.25em] font-bold">
                  Most Chosen
                </div>
              )}

              <div className="mono-label mb-3">{t.kicker}</div>
              <h3 className="serif-head text-3xl mb-1">{t.name}</h3>
              <p className="mono-body text-[12px] mb-6">{t.tagline}</p>

              <div className="border-y border-white/10 py-5 mb-6">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="serif-head text-3xl md:text-4xl lg:text-5xl text-nex-silver">{t.price}</span>
                  {t.priceHigh && <span className="serif-head text-xl md:text-2xl text-nex-chrome">–</span>}
                  {t.priceHigh && <span className="serif-head text-2xl md:text-3xl lg:text-4xl text-nex-gold">{t.priceHigh}</span>}
                </div>
                <div className="flex justify-between mono-body text-[11px]">
                  <span>{t.unit}</span>
                  <span>delivery · <span className="text-nex-silver">{t.delivery}</span></span>
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {t.includes.map((inc, k) => (
                  <li key={k} className="flex items-start gap-3 text-nex-silver text-sm">
                    <Check size={14} className="text-nex-gold shrink-0 mt-1" />
                    <span>{inc}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => smart("#friction-audit")}
                data-testid={`pricing-cta-${t.id}`}
                className={t.feature ? "tick-btn-primary justify-center w-full" : "tick-btn-secondary justify-center w-full"}
              >
                {t.cta} <ArrowRight size={14} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
